// JornadasAgotadas.jsx
//
// #1887 · pantalla `jornadas-agotadas` (spec `.claude/specs/jornada-suelta/`, T28, R35). Se abre con el 403 `sin_jornadas` de
// cualquier punto de IA (lo atiende `JornadaAvisosGate`). NO es `SuscripcionGate`: no tapa la app, lo escrito sigue ahí y leer,
// copiar, editar y guardar siguen funcionando (R52); solo se pausa generar con IA. Dos salidas nuevas respecto de #1605: sumar
// una jornada adicional (`suelta-comprar`, solo si `GET /me/suelta/` dice `disponible`) y subir de plan; y «Seguir sin generar».
// Diseño: el de la variante D del mockup de #1605 (`feat/1605-front-p3`, «Sin jornadas este período»: tarjeta con la garantía de
// que lo escrito sigue ahí y las salidas), sin tramos ni WhatsApp. Este spec no trae un mockup propio de esta pantalla (hueco
// anotado en progress).
// Cubre R35 — .claude/specs/jornada-suelta/
import { useEffect, useState } from "react";
import { Box, Button, Dialog, Typography, useMediaQuery } from "@mui/material";
import GppGoodOutlinedIcon from "@mui/icons-material/GppGoodOutlined";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import useJornadaEstado from "../../hooks/useJornadaEstado";
import CheckoutDialog from "../checkout/CheckoutDialog";
import { abrirCompraSuelta, avisarAgotadas, diaMes, preciosTxt } from "../../helpers/suelta-api";
import { botonGhostSx, botonPrimarioSx } from "../checkout/botonesSx";
import { Z_ENCIMA_DEL_GATE } from "../jornada/estilos";

const JornadasAgotadas = ({ abierta, onCerrar }) => {
  const { jornada, oferta, recargar } = useJornadaEstado({ auto: false });
  const movil = useMediaQuery("(max-width: 768px)");
  const [eligiendoPlan, setEligiendoPlan] = useState(false);

  useEffect(() => {
    if (abierta) recargar();
  }, [abierta, recargar]);

  // Mientras esta pantalla está abierta, la banda de /turno de atrás esconde sus botones (ver `avisarAgotadas`).
  useEffect(() => {
    avisarAgotadas(abierta);
    return () => avisarAgotadas(false);
  }, [abierta]);

  if (!abierta) return null;
  const periodo = jornada?.periodo;
  const fin = periodo?.fin ? new Date(new Date(`${periodo.fin}T12:00:00-03:00`).getTime() + 24 * 3600 * 1000).toISOString().slice(0, 10) : "";
  const subtitulo =
    periodo && periodo.incluidas !== null && periodo.incluidas !== undefined
      ? `${periodo.usadas} de ${periodo.incluidas} usadas. Se renueva el ${diaMes(fin)}.`
      : "Se renueva con el próximo período.";
  const puedeComprar = Boolean(oferta?.disponible);
  const precio = puedeComprar ? preciosTxt(oferta) : "";

  return (
    <>
      <Dialog
        open
        onClose={onCerrar}
        fullWidth
        maxWidth={false}
        sx={{ zIndex: Z_ENCIMA_DEL_GATE, "& .MuiBackdrop-root": { backgroundColor: "rgba(var(--shadow-rgb),0.45)" } }}
        PaperProps={{
          "data-testid": "jornadas-agotadas",
          sx: {
            width: movil ? "100%" : 460,
            maxWidth: movil ? "100%" : "92%",
            m: 0,
            p: movil ? "10px 16px 18px" : "20px",
            backgroundColor: "var(--bg-elevated)",
            backgroundImage: "none",
            border: "1px solid var(--border-strong)",
            boxShadow: "0 20px 50px rgba(var(--shadow-rgb),0.5)",
            ...(movil
              ? { position: "fixed", bottom: 0, left: 0, right: 0, borderRadius: "18px 18px 0 0", borderBottom: 0 }
              : { borderRadius: "18px" }),
          },
        }}
      >
        {movil && <Box sx={{ width: 38, height: 4, borderRadius: "3px", backgroundColor: "rgba(var(--fg-rgb),0.2)", mx: "auto", mb: "12px" }} />}
        <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          <Box>
            <Typography sx={{ fontSize: 19, fontWeight: 700, lineHeight: 1.2, color: "var(--text-primary)" }}>Sin jornadas este período</Typography>
            <Typography sx={{ fontSize: 12.5, color: "var(--text-muted)", mt: "3px" }}>{subtitulo}</Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              gap: "10px",
              alignItems: "flex-start",
              p: "12px 14px",
              borderRadius: "12px",
              backgroundColor: "rgba(var(--color-success-rgb),0.08)",
              border: "1px solid rgba(var(--color-success-rgb),0.25)",
              fontSize: 12.5,
              color: "var(--text-secondary)",
            }}
          >
            <Box sx={{ color: "var(--color-success)", mt: "2px", display: "flex" }}>
              <GppGoodOutlinedIcon sx={{ fontSize: 18 }} />
            </Box>
            <Box>
              <b>Lo que ya escribiste sigue aquí.</b> Puedes seguir leyendo, copiando, editando y guardando. Solo se pausa generar con IA.
            </Box>
          </Box>
          {puedeComprar && (
            <Box>
              <Button
                data-testid="suelta-comprar"
                variant="contained"
                disableElevation
                fullWidth
                startIcon={<AddCircleOutlineIcon />}
                onClick={() => {
                  onCerrar();
                  abrirCompraSuelta();
                }}
                sx={{ ...botonPrimarioSx, py: 1.1, fontSize: 13.5 }}
              >
                Sumar jornada adicional
              </Button>
              {precio && <Typography sx={{ fontSize: 11.5, color: "var(--text-faint)", mt: 0.75, textAlign: "center" }}>{`Jornada adicional, sin cambiar de plan: ${precio}.`}</Typography>}
            </Box>
          )}
          <Button data-testid="jornadas-subir-plan" variant="outlined" fullWidth onClick={() => setEligiendoPlan(true)} sx={{ ...botonGhostSx, py: 1.1, fontSize: 13.5 }}>
            Subir de plan
          </Button>
          <Box sx={{ textAlign: "center" }}>
            <Box
              component="button"
              type="button"
              data-testid="jornadas-seguir-sin-generar"
              onClick={onCerrar}
              sx={{ border: 0, background: "transparent", cursor: "pointer", font: "inherit", fontSize: 12.5, fontWeight: 600, color: "var(--color-primary-soft)" }}
            >
              Seguir sin generar
            </Box>
          </Box>
        </Box>
      </Dialog>
      {eligiendoPlan ? <CheckoutDialog open onClose={() => setEligiendoPlan(false)} /> : null}
    </>
  );
};

export default JornadasAgotadas;
